import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FileModel, ListSearch, RunSearchOptions, SortOption } from '@ca/core/models';
import deepEqual from 'deep-equal';
import { OptionItem } from 'src/app/core/models/option-item.model';
import { WorkflowStateService } from 'src/app/core/services/workflow-state.service';
import * as FileSearchSortUtils from '../../../../utils/file-search-sort/file-search-sort-utils';

@Component({
    selector: 'ca-file-selection',
    templateUrl: './file-selection.component.html',
    styleUrls: ['./file-selection.component.scss'],
})
export class FileSelectionComponent implements OnInit, OnChanges {
    /**
     * All selected files
     */
    @Input() selectedFiles: FileModel[] = [];
    @Input() isDisabled: boolean;
    @Input() showViewAllButton: boolean = true;
    @Input() showPreviewMode: boolean = false;
    @Input() showViewIconForSelectedItems: boolean = true;
    @Input() showFileSize: boolean = true;
    @Input() filesSelectable: boolean = false;
    @Input() flowId: string;
    @Input() isCziConversionInProgress: boolean = false;
    @Input() numberOfConversionFailedFiles: number = 0;

    /**
     * Pass a ListSearch object here to predefine the search and sort state.
     * If undefined, defaultSearchOptions is taken.
     * You can also change this object from outside, the component will react to the changes and retrigger search.
     */
    @Input() searchAndSortOptions?: ListSearch;

    @Output() onRemoveFile = new EventEmitter<FileModel>();
    @Output() onRemoveAllFiles = new EventEmitter<void>();
    @Output() onReset = new EventEmitter<void>();
    @Output() onAddFiles = new EventEmitter<void>();
    @Output() onViewSelection = new EventEmitter<void>();
    @Output() onSelectedFileChange = new EventEmitter<FileModel | null>();
    @Output() onUpdateFileUrl = new EventEmitter<string>();

    /**
     * Emits the current set of search params, when they're changed
     */
    @Output() onChangeSearchAndSortOptions = new EventEmitter<ListSearch>();

    selectedIndex: number | null = null;

    // 417728: Removed sort by date options
    sortOptions: OptionItem<SortOption>[] = [
        { label: 'Sort by A-Z', value: 'name' },
        { label: 'Sort by Z-A', value: '-name' },
    ];
    /**
     * Filtered result (i.e. by search term). Subset of this.selectedFiles
     */
    filteredSelectedFiles: FileModel[] = [];
    /**
     * selected files from current page. Subset of this.filteredSelectedFiles
     */
    pagedSelectedFiles: FileModel[] = [];
    defaultPageSize = 10;
    defaultSearchOptions: ListSearch = {
        pageNumber: 1,
        pageSize: this.defaultPageSize,
        searchQuery: '',
        sortBy: '',
    };
    currentSearchOptions: ListSearch | undefined;

    constructor(private workflowStateService: WorkflowStateService) {}

    // Fetch current sort parameters for this run step from the state
    get stateRunSearchObject(): RunSearchOptions {
        if (this.workflowStateService.runSearchObject[this.flowId] == null) {
            this.workflowStateService.runSearchObject[this.flowId] = new RunSearchOptions();
        }
        return this.workflowStateService.runSearchObject[this.flowId];
    }

    ngOnInit(): void {
        const stateSearchOptions: ListSearch | undefined = this.stateRunSearchObject.selectedFilesCurrentSearchOptions;
        if (stateSearchOptions != null && !deepEqual(this.currentSearchOptions, stateSearchOptions)) {
            this.currentSearchOptions = stateSearchOptions;
            this.searchAndSortOptions = stateSearchOptions;
        } else {
            // First, apply the search terms (take the default ones if not provided from outside)
            if (this.searchAndSortOptions != null) {
                this.currentSearchOptions = this.searchAndSortOptions;
            } else {
                this.currentSearchOptions = this.defaultSearchOptions;
            }
            this.workflowStateService.runSearchObject[this.flowId].selectedFilesCurrentSearchOptions = this.currentSearchOptions;
        }
        this.prepareDisplayFiles();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedFiles != null) {
            this.prepareDisplayFiles();
        }
        if (changes.searchAndSortOptions != null) {
            this.updateSearchAndSortOptions(changes.searchAndSortOptions.currentValue as ListSearch, false);
        }
    }

    onFileSearchOrSort(params: ListSearch): void {
        const stateSearchOptions: ListSearch | undefined = this.stateRunSearchObject.selectedFilesCurrentSearchOptions;
        if (!deepEqual(params, stateSearchOptions)) {
            this.currentSearchOptions = params;
            this.workflowStateService.runSearchObject[this.flowId].selectedFilesCurrentSearchOptions = params;
        }
        // First, update search and sort params. This method will dispatch onChangeSearchAndSortOptions event,
        // which will be picked up in starter, and starter will set searchAndSortOptions.
        // This then triggers ngOnChanges in ALL OTHER (but not the current) ListSelection components (and updates searchAndSortOptions)
        // This is needed because we have two instances of FileSelectionComponent in WorkflowStarter, and they need to be in sync (417728)
        this.updateSearchAndSortOptions(params);
    }

    remove(file: FileModel): void {
        if (!this.selectedFiles.some((item: FileModel) => file.token === item.token)) {
            return;
        }
        this.onRemoveFile.emit(file);
    }

    removeAll(): void {
        this.onRemoveAllFiles.emit();
    }

    resetFiles(): void {
        this.workflowStateService.clearSelectedFilesSearchOptions(this.flowId);
        this.currentSearchOptions = this.defaultSearchOptions;
        this.searchAndSortOptions = this.defaultSearchOptions;
        this.onReset.emit();
    }

    handleAddFiles(): void {
        this.onAddFiles.emit();
    }

    viewSelection(): void {
        this.onViewSelection.emit();
    }

    handleFileClick(index: number | null, preselectFirst: boolean = false): void {
        if (!this.showPreviewMode) {
            return;
        }
        if (index != null) {
            if (this.selectedIndex === index) {
                // if we want to preselect the first file and keep it selected
                if (this.selectedIndex === 0 && index === 0 && preselectFirst) {
                    this.selectedIndex = index;
                } else {
                    this.selectedIndex = null;
                }
            } else {
                this.selectedIndex = index;
            }
        } else {
            this.selectedIndex = 0;
        }
        setTimeout(() => {
            if (this.selectedIndex == null) {
                this.onSelectedFileChange.emit(undefined);
            } else if (this.selectedIndex != null && this.selectedFiles.length > this.selectedIndex) {
                this.onSelectedFileChange.emit(this.pagedSelectedFiles[this.selectedIndex]);
            }
        });
    }

    private prepareDisplayFiles(): void {
        this.filteredSelectedFiles = FileSearchSortUtils.getShownFilesBySearchAndSortOptions(this.selectedFiles, this.currentSearchOptions);
        this.pagedSelectedFiles = FileSearchSortUtils.getCurrentPageOfFiles(this.filteredSelectedFiles, this.currentSearchOptions);
        // preselect first file - also dispatches event that selected files changed
        if (this.showPreviewMode) {
            this.selectedIndex = 0;
            this.handleFileClick(this.selectedIndex, true);
        }
    }

    private updateSearchAndSortOptions(params: ListSearch, emitChange: boolean = true): void {
        this.currentSearchOptions = params;
        this.prepareDisplayFiles();
        if (emitChange) {
            this.onChangeSearchAndSortOptions.emit(params);
        }
    }
}
